import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getServerUrl, environment } from '../config';

// Get the Stripe server URL based on environment
const getStripeServerUrl = () => {
  if (environment === 'production') {
    // In production, use the domain without port
    return 'https://stripe.geteditify.com';
  } else {
    // In development, use the dynamic URL with port
    return getServerUrl('stripe');
  }
};

// API URL for the Stripe server
const STRIPE_SERVER_URL = getStripeServerUrl();

// Log the Stripe server URL for debugging
console.log(`Using Stripe server URL: ${STRIPE_SERVER_URL}`);

// Default Stripe settings
const DEFAULT_STRIPE_SETTINGS = {
  apiKey: '',
  publishableKey: '',
  webhookSecret: '',
  testMode: true,
  updatedAt: new Date().toISOString()
};

// Initialize Stripe settings in Firestore if they don't exist
export const initializeStripeSettings = async () => {
  try {
    const settingsRef = doc(db, 'system_settings', 'stripe');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await setDoc(settingsRef, DEFAULT_STRIPE_SETTINGS);
      console.log('Stripe settings initialized');
    }
    
    return true;
  } catch (error) {
    console.error('Error initializing Stripe settings:', error);
    return false;
  }
};

// Get Stripe settings
export const getStripeSettings = async () => {
  try {
    const settingsRef = doc(db, 'system_settings', 'stripe');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await initializeStripeSettings();
      return DEFAULT_STRIPE_SETTINGS;
    }
    
    return settingsDoc.data();
  } catch (error) {
    console.error('Error getting Stripe settings:', error);
    return DEFAULT_STRIPE_SETTINGS;
  }
};

// Update Stripe settings
export const updateStripeSettings = async (updates) => {
  try {
    const settingsRef = doc(db, 'system_settings', 'stripe');
    
    // Check if settings exist
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      // Create settings if they don't exist
      await setDoc(settingsRef, {
        ...DEFAULT_STRIPE_SETTINGS,
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } else {
      // Update existing settings
      await updateDoc(settingsRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating Stripe settings:', error);
    return false;
  }
};

// Load Stripe.js script
export const loadStripeScript = async () => {
  try {
    const settings = await getStripeSettings();
    if (!settings.publishableKey) {
      throw new Error('Stripe publishable key not found');
    }
    
    return new Promise((resolve, reject) => {
      if (window.Stripe) {
        resolve(window.Stripe(settings.publishableKey));
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        if (window.Stripe) {
          resolve(window.Stripe(settings.publishableKey));
        } else {
          reject(new Error('Stripe.js failed to load'));
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load Stripe.js'));
      };
      
      document.head.appendChild(script);
    });
  } catch (error) {
    console.error('Error loading Stripe script:', error);
    throw error;
  }
};

// Create a Stripe checkout session
export const createCheckoutSession = async (userId, items, successUrl, cancelUrl) => {
  try {
    const response = await fetch(`${STRIPE_SERVER_URL}/create-flex-credits-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        packageId: items.packageType,
        successUrl,
        cancelUrl
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return { success: false, error: error.message };
  }
};

// Create a Stripe subscription
export const createSubscription = async (userId, planType, successUrl, cancelUrl, billingPeriod = 'monthly', isTrial = false) => {
  try {
    console.log(`Creating subscription with Stripe server: ${STRIPE_SERVER_URL}`);
    console.log(`Params: userId=${userId}, planType=${planType}, billingPeriod=${billingPeriod}`);
    
    const response = await fetch(`${STRIPE_SERVER_URL}/${isTrial ? 'create-trial-subscription' : 'create-subscription-checkout'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        planType,
        successUrl,
        cancelUrl,
        billingPeriod,
        isTrial
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Subscription creation result:', result);
    return result;
  } catch (error) {
    console.error('Error creating subscription:', error);
    return { success: false, error: error.message };
  }
};

// Check if a user is eligible for a trial
export const checkTrialEligibility = async (userId) => {
  try {
    const response = await fetch(`${STRIPE_SERVER_URL}/check-trial-eligibility`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error checking trial eligibility:', error);
    return { eligible: false, reason: error.message };
  }
};

// Cancel a Stripe subscription
export const cancelSubscription = async (userId, subscriptionId) => {
  try {
    console.log(`Canceling subscription with Stripe server: ${STRIPE_SERVER_URL}`);
    console.log(`Params: userId=${userId}, subscriptionId=${subscriptionId}`);
    
    const response = await fetch(`${STRIPE_SERVER_URL}/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        subscriptionId
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Stripe server response error:', errorText);
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || `Failed to cancel subscription: ${response.status} ${response.statusText}`);
      } catch (jsonError) {
        throw new Error(`Failed to cancel subscription: ${response.status} ${response.statusText}`);
      }
    }
    
    const result = await response.json();
    console.log('Subscription cancellation result:', result);
    return result;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    return { success: false, error: error.message };
  }
};

// Redirect to Stripe Checkout
export const redirectToCheckout = async (sessionId) => {
  try {
    console.log(`Redirecting to Stripe checkout with sessionId: ${sessionId}`);
    
    // Get the session URL from the result if available
    if (sessionId.startsWith('http')) {
      console.log('Using direct URL for checkout');
      window.location.href = sessionId;
      return { success: true };
    }
    
    // Load Stripe.js
    const stripe = await loadStripeScript();
    
    if (!stripe) {
      throw new Error('Failed to load Stripe.js');
    }
    
    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionId
    });
    
    if (error) {
      throw new Error(error.message);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    return { success: false, error: error.message };
  }
};
